import { APP_DOMAIN } from 'utils/urls';

import { HandHistoryEvent } from './types';

// The server sends `avatar` as a bare filename; the seats and Chat build the
// same URL (components/Seat/SeatWithPlayer, components/Chat/Chat.tsx).
const MEDIA_URL = `https://media.${APP_DOMAIN}/images/`;

/**
 * A server-sent avatar (filename, absolute URL, data/blob URI, or empty) → an
 * <img src>, or `undefined` when there is nothing to load. Callers render MUI's
 * Avatar, which shows the player's initial when there is no source or the
 * image fails - the same fallback Game Chat uses, not a stand-in picture.
 */
export const resolveAvatarSrc = (avatar: string | undefined): string | undefined => {
  const normalizedAvatar = avatar?.trim();
  if (!normalizedAvatar) return undefined;

  return /^(https?:|data:|blob:)/i.test(normalizedAvatar)
    ? normalizedAvatar
    : `${MEDIA_URL}${normalizedAvatar}`;
};

/** Display name → avatar source (or `undefined` for a player with no picture). */
export type AvatarByName = Record<string, string | undefined>;

/**
 * Builds the name → avatar map from any list of people (a completed hand's
 * seats, the live table's players). Names are kept even without a picture, so
 * their rows still get an avatar with an initial. Later entries do not
 * overwrite earlier ones, so a caller can put the most authoritative list first.
 */
export const buildAvatarByName = (
  people: Array<{ name?: string; displayName?: string; avatar?: string } | null | undefined>
): AvatarByName =>
  people.reduce<AvatarByName>((byName, person) => {
    const name = person?.name || person?.displayName;
    if (name && !(name in byName)) byName[name] = resolveAvatarSrc(person?.avatar);
    return byName;
  }, {});

export interface EventPlayer {
  name: string;
  src?: string;
}

/**
 * The player behind a play-by-play row, matching the production Table History:
 * a player's action row carries their avatar, a system row ("Dealing Flop",
 * "Game starting...") carries none. Events name their player when the server
 * captured it; the live stream does not, so fall back to the display name the
 * text starts with. Names may contain spaces, so the longest known name that
 * prefixes the text wins ("Player 3 folds" → "Player 3", not "Player").
 */
export const resolveEventPlayer = (
  event: Pick<HandHistoryEvent, 'text' | 'playerName'>,
  avatarByName?: AvatarByName
): EventPlayer | undefined => {
  if (!avatarByName) return undefined;
  if (event.playerName && event.playerName in avatarByName) {
    return { name: event.playerName, src: avatarByName[event.playerName] };
  }

  const text = event.text?.trim() ?? '';
  if (!text) return undefined;
  const match = Object.keys(avatarByName)
    .filter((name) => text === name || text.startsWith(`${name} `))
    .sort((a, b) => b.length - a.length)[0];
  return match ? { name: match, src: avatarByName[match] } : undefined;
};

/** The single character an avatar shows when there is no picture. */
export const avatarInitial = (name: string) => name.trim().charAt(0).toUpperCase() || '?';
